import { CallPatient } from ".";
import { type Call, type CallStatus, SignalWire, StaticCredentialProvider } from "@signalwire/js";
import type { Subscription } from "rxjs";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { FaExclamationCircle, FaPhoneAlt, FaPhoneSlash } from "react-icons/fa";
import CallControls from "@/components/call/call-controls";
import { CallScribeButton, CallScribeSection, useCallScribe } from "@/components/call/call-scribe";
import DialedDigitsDisplay from "@/components/call/dialed-digits-display";
import DtmfKeypad from "@/components/call/dtmf-keypad";
import { type OutboundCallEndedBy, type OutboundCallSetupStage, captureOutboundCallAttempted, captureOutboundCallEnded, captureOutboundCallSetupFailed } from "@/components/call/outbound-call-telemetry";
import { useCallEventListeners } from "@/components/call/use-call-event-listeners";
import { useCallMute } from "@/components/call/use-call-mute";
import { formatTime, useCallTimer } from "@/components/call/use-call-timer";
import { useDTMF } from "@/components/call/use-dtmf";
import { useRemoteAudio } from "@/components/call/use-remote-audio";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { formatPhoneNumberE164 } from "@/lib/format-data";
import { requestNativeCall } from "@/lib/native-bridge";
import { SIGNALWIRE_PSTN_AUDIO_CODECS, applyPstnAudioCodecPreference } from "@/lib/signalwire";
import { toast } from "@/lib/use-toast";
import { fetchOutboundCallerId, fetchVoiceToken } from "@/query/comms/chat";
import { useActiveCallStore } from "@/stores/active-call-store";
const SIGNALWIRE_MANUAL_CALL_RESOURCE = process.env.NEXT_PUBLIC_SIGNALWIRE_MANUAL_CALL_RESOURCE || "/public/prod-manual-call-swml";
interface ManualCallProps {
  patient: CallPatient;
  onCallEnd: (result?: CallEndResult) => void;
}

/** How a call ended, as far as the browser could tell. */
export interface CallEndResult {
  /** Someone (or a voicemail) answered; null when it can't be known. */
  pickedUp: boolean | null;
  /** The phone app took the call over, so it hasn't actually ended. */
  handedOff?: boolean;
}
const ManualCall: React.FC<ManualCallProps> = ({
  patient,
  onCallEnd
}) => {
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  // Stable per-mount id for this outbound leg in the shared active-call
  // store. The modal is mounted for exactly the lifetime of one call.
  const legId = useId();
  const beginActiveCall = useActiveCallStore(s => s.beginCall);
  const endActiveCall = useActiveCallStore(s_0 => s_0.endCall);
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  // Backend Call id, obtained from /call/token before dialing so the in-call
  // "save visit as note" toggle has a stable key.
  const [callId, setCallId] = useState<string | null>(null);
  const [callState, setCallState] = useState("");
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  // Ref mirror of localStream for the unmount cleanup, which closes over
  // the first render where the state value is still null.
  const localStreamRef = useRef<MediaStream | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const scribe = useCallScribe({
    callId,
    patientId: patient.id
  });
  const {
    elapsedTime,
    startTimer,
    stopTimer,
    timerInterval
  } = useCallTimer();
  const {
    isMuted,
    toggleMute,
    resetMute
  } = useCallMute({
    call: currentCall
  });
  const {
    dialedDigits,
    sendDTMF,
    resetDialedDigits
  } = useDTMF({
    call: currentCall,
    isCallActive: useCallback(() => callState === "On Call", [callState])
  });
  useRemoteAudio(currentCall);
  const getCallStateIcon = () => {
    switch (callState) {
      case "Connecting...":
        return <FaPhoneSlash />;
      case "On Call":
        return <FaPhoneAlt />;
      case "Ended":
        return <FaPhoneSlash />;
      case "Error":
        return <FaExclamationCircle />;
      default:
        return null;
    }
  };
  const requestMicrophoneAccess = async (): Promise<MediaStream | null> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      return stream;
    } catch (error) {
      console.error("Microphone access error:", error);
      captureOutboundCallSetupFailed("microphone", error);
      const errorMessage = (error as Error).name === "NotAllowedError" ? "Microphone access was denied. Please enable microphone access in your browser settings and try again." : "Unable to access microphone. Please check if your device has a working microphone.";
      toast({
        title: "Microphone Error",
        description: errorMessage,
        variant: "error"
      });
      return null;
    }
  };

  // Set once the modal unmounts, so an in-flight makeManualCall can bail
  // instead of landing a live call nobody owns.
  const isUnmountedRef = useRef(false);
  // Track if we've already cleaned up to prevent duplicate calls
  const hasCleanedUpRef = useRef(false);
  // Store call ref for cleanup
  const callRef = useRef<Call | null>(null);
  // Track if timer has been started (call answered)
  const timerStartedRef = useRef(false);
  // Store memberJoined subscription for cleanup
  const memberJoinedSubRef = useRef<Subscription | null>(null);

  // Telemetry state for the dialed call. Refs, not state: the unmount
  // cleanup and the memberJoined subscription both close over the first
  // render.
  const telemetryCallIdRef = useRef<string | null>(null);
  const dialedAtRef = useRef<number | null>(null);
  const browserLegConnectedRef = useRef(false);
  const calleeJoinedRef = useRef(false);
  const userHungUpRef = useRef(false);
  const endReportedRef = useRef(false);
  const reportCallEnded = useCallback((endedBy: OutboundCallEndedBy, finalStatus: CallStatus | null) => {
    const callId_0 = telemetryCallIdRef.current;
    const dialedAt = dialedAtRef.current;
    if (endReportedRef.current || callId_0 === null || dialedAt === null) {
      return;
    }
    endReportedRef.current = true;
    captureOutboundCallEnded({
      call_id: callId_0,
      ended_by: endedBy,
      final_status: finalStatus,
      browser_leg_connected: browserLegConnectedRef.current,
      callee_joined: calleeJoinedRef.current,
      seconds_since_dial: Math.round((Date.now() - dialedAt) / 1000)
    });
  }, []);

  // Shared cleanup function to handle call end
  const handleCallEnded = useCallback((status?: CallStatus) => {
    // Prevent duplicate cleanup
    if (hasCleanedUpRef.current) {
      return;
    }
    hasCleanedUpRef.current = true;
    reportCallEnded(userHungUpRef.current ? "user" : "remote", status ?? null);
    endActiveCall(legId);
    setCallState("Ended");
    stopTimer();
    timerStartedRef.current = false;
    resetMute();
    resetDialedDigits();

    // Clean up memberJoined subscription
    memberJoinedSubRef.current?.unsubscribe();
    memberJoinedSubRef.current = null;

    // Clean up local stream
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    localStreamRef.current = null;

    // The call is over — by the user hanging up (hangUpCall already called
    // hangup()) or by the remote end. Clearing the ref keeps the invariant
    // that it only ever holds a *live* call, so the unmount cleanup below
    // doesn't hang up a second time on a leg that's already gone.
    callRef.current = null;

    // Call the parent callback. Whether anyone picked up is only known when
    // our own leg connected; a dial that never got going says nothing.
    onCallEnd({
      pickedUp: browserLegConnectedRef.current ? calleeJoinedRef.current : null
    });
  }, [localStream, onCallEnd, stopTimer, resetMute, resetDialedDigits, endActiveCall, legId, reportCallEnded]);

  // Handle call connected (PSTN party answered)
  const handleConnected = useCallback(() => {
    browserLegConnectedRef.current = true;
    if (!timerStartedRef.current) {
      timerStartedRef.current = true;
      setCallState("On Call");
      startTimer();
    }
  }, [startTimer]);

  // Use shared hook for call status event listeners
  useCallEventListeners({
    call: currentCall,
    onCallEnded: handleCallEnded,
    onConnected: handleConnected
  });

  // Release everything claimed so far when the modal goes away mid-dial.
  const abandonDial = (stream_0: MediaStream | null) => {
    stream_0?.getTracks().forEach(track_0 => track_0.stop());
    endActiveCall(legId);
  };
  const makeManualCall = async () => {
    if (typeof window === "undefined") return;

    // Embedded in the native mobile app: hand the call to the native engine so
    // it rings through CallKit, takes audio-session priority, appears in the
    // system call log and survives backgrounding. A WebView cannot do any of
    // that, and its WebRTC audio loses to a real incoming phone call.
    // Falls through to the browser path in an ordinary browser.
    if (requestNativeCall({
      e164: formatPhoneNumberE164(patient.phoneNumber),
      patientId: patient.id,
      displayName: [patient.firstName, patient.lastName].filter(Boolean).join(" ")
    })) {
      onCallEnd({
        pickedUp: null,
        handedOff: true
      });
      return;
    }

    // Claim the user as busy up front, before the mic prompt and before the
    // callee picks up. An inbound call arriving mid-dial is the disruption
    // we're guarding against, so the claim has to cover the whole attempt.
    beginActiveCall(legId, "outbound");
    captureOutboundCallAttempted();
    const stream_1 = await requestMicrophoneAccess();
    if (!stream_1) {
      endActiveCall(legId);
      return;
    }
    if (isUnmountedRef.current) {
      abandonDial(stream_1);
      return;
    }
    setCallState("Connecting...");
    let stage: OutboundCallSetupStage = "token";
    try {
      // A failed caller-ID lookup must not block the call; it falls back to
      // the clinic's main number below.
      const [tokenResponse, callerId] = await Promise.all([fetchVoiceToken(), fetchOutboundCallerId(patient.id).catch(() => null)]);
      const voiceToken = tokenResponse?.token;
      // Get fresh authentication token
      if (!voiceToken || !tokenResponse) {
        throw new Error("No token returned");
      }
      // Backend-generated Call id: makes the resulting Call row deterministic
      // and is the key for the in-call "save visit as note" toggle.
      const backendCallId = tokenResponse.call_id;
      if (isUnmountedRef.current) {
        abandonDial(stream_1);
        return;
      }
      setCallId(backendCallId);
      telemetryCallIdRef.current = backendCallId;
      stage = "dial";

      // Initialize SignalWire client (dial() waits for connection internally)
      const client = new SignalWire(new StaticCredentialProvider({
        token: voiceToken
      }), {
        skipRegister: true
      });
      applyPstnAudioCodecPreference(client);

      // Format patient phone to E.164 for SWML
      const patientPhoneE164 = formatPhoneNumberE164(patient.phoneNumber);

      // Set user variables on client preferences BEFORE dialing so they
      // are included in the initial call signaling and available to the
      // SWML script immediately.
      client.preferences.userVariables = {
        destination_number: patientPhoneE164,
        patient_id: patient.id,
        sender_id: currentUser!.id,
        tenant: tenant!.subDomain,
        tenant_number: callerId ?? formatPhoneNumberE164(tenant!.phoneNumber),
        // Key for the AI-scribe intent toggle. Deliberately NOT `call_id`:
        // for manual outbound calls the backend Call.id is SignalWire's own
        // call id (resolved from call_info), and shadowing `call_id` here would
        // break the transcript webhook's call lookup. The webhook reads this
        // separate var to find the CallScribeIntent the toggle persisted.
        scribe_call_id: backendCallId
      };

      // Dial the SWML resource, passing the pre-captured mic stream
      // so the SDK uses it directly instead of calling getUserMedia again.
      const call = await client.dial(SIGNALWIRE_MANUAL_CALL_RESOURCE, {
        audio: true,
        inputAudioStream: stream_1,
        preferredAudioCodecs: [...SIGNALWIRE_PSTN_AUDIO_CODECS],
        video: false
      });
      dialedAtRef.current = Date.now();

      // The modal closed while the SDK was dialing: the leg is live but
      // has no owner, so tear it down rather than leaving a call running
      // with no busy claim behind it.
      if (isUnmountedRef.current) {
        reportCallEnded("modal_closed", null);
        await call.hangup().catch(error_1 => {
          console.error("Error hanging up abandoned call:", error_1);
        });
        abandonDial(stream_1);
        return;
      }

      // Store call ref for cleanup and event listeners
      callRef.current = call;
      setCurrentCall(call);
      setCallState("Ringing...");

      // Get the actual stream SignalWire is using
      const activeStream = call.localStream || stream_1;
      setLocalStream(activeStream);
      localStreamRef.current = activeStream;

      // Listen for member.joined - fires when PSTN party picks up
      // Access the internal memberJoined$ observable from WebRTCCall
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mgr = call as any;
      if (mgr.memberJoined$) {
        memberJoinedSubRef.current = mgr.memberJoined$.subscribe(() => {
          calleeJoinedRef.current = true;
          if (!timerStartedRef.current) {
            timerStartedRef.current = true;
            setCallState("On Call");
            startTimer();
          }
        }) as Subscription;
      }
    } catch (error_0) {
      console.error("Call setup error:", error_0);
      captureOutboundCallSetupFailed(stage, error_0, {
        call_id: telemetryCallIdRef.current
      });
      endActiveCall(legId);
      setCallState("Error");
      toast({
        title: "Error",
        description: "An error occurred while setting up the call.",
        variant: "error"
      });
    }
  };
  const hangUpCall = () => {
    if (currentCall) {
      userHungUpRef.current = true;
      try {
        currentCall.hangup();
      } catch (error_2) {
        console.error("Error leaving call:", error_2);
      }
      handleCallEnded();
    }
  };
  useEffect(() => {
    makeManualCall();
    return () => {
      isUnmountedRef.current = true;
      try {
        if (timerInterval.current) {
          clearInterval(timerInterval.current);
        }

        // Unsubscribe from memberJoined observable
        memberJoinedSubRef.current?.unsubscribe();
        memberJoinedSubRef.current = null;

        // Read through refs, not state: this cleanup closes over the first
        // render, where `currentCall` and `localStream` are both still
        // null, so the live call was never actually being torn down here.
        // hangup() returns a promise, so its rejection needs a handler
        // rather than a synchronous try/catch.
        if (callRef.current) {
          reportCallEnded("modal_closed", null);
        }
        callRef.current?.hangup().catch(error => {
          console.error("Error leaving call:", error);
        });
        callRef.current = null;
        localStreamRef.current?.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      } finally {
        // In a `finally` so nothing above can skip it. Deliberately not
        // chained onto the hangup promise either: handleCallEnded is
        // guarded by hasCleanedUpRef and doesn't run on every path, and a
        // hangup that throws or never settles must not leave the user
        // permanently marked busy — that would silently suppress every
        // future inbound call.
        endActiveCall(legId);
      }
    };
  }, []);
  return <div className="flex w-full flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-1">
          {getCallStateIcon()}
          <span>Call State: {callState}</span>
        </div>
        <span>{formatTime(elapsedTime)}</span>
      </div>
      <CallScribeSection scribe={scribe} />
      <div className={`flex gap-4 ${showKeypad ? "flex-row" : "flex-col"}`}>
        {/* Left side - Call controls and dialed digits */}
        <div className="flex flex-1 flex-col gap-2">
          <CallControls isMuted={isMuted} onToggleMute={toggleMute} onHangup={hangUpCall} showKeypad={showKeypad} onToggleKeypad={() => setShowKeypad(!showKeypad)}
        // Keep the call open while a scribe-intent request is in flight so
        // it can't end before the intent is persisted.
        hangupDisabled={!currentCall || scribe.isSaving} scribeMenu={<CallScribeButton scribe={scribe} />} />
          <DialedDigitsDisplay digits={dialedDigits} />
        </div>
        {/* Right side - Keypad */}
        {showKeypad && <DtmfKeypad onSendDigits={sendDTMF} disabled={!currentCall || callState !== "On Call"} />}
      </div>
    </div>;
};
export default ManualCall;